import { Currency, Money, UsdCurrency } from "@redotech/money";
import { useRequiredContext } from "@redotech/react-util/context";
import { useEventListener } from "@redotech/react-util/event";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { getCollections, getFees } from "@redotech/redo-api-client/widget";
import {
  DEFAULT_IMAGE_URL,
  defaultToggleTextOptions,
  fillEmptyFieldsWithDefault,
} from "@redotech/redo-model/default-values";
import { WidgetAutocheck, WidgetFees } from "@redotech/redo-model/widget";
import { WidgetClientContext } from "@redotech/redo-web/client";
import { ModalRootContext } from "@redotech/redo-web/modal";
import type { ShopifyProduct } from "@redotech/shopify-client/ajax";
import {
  ExtensionShopifyClient,
  ShopifyCartItem,
  ShopifyClientType,
  ShopifyProductJS,
} from "@redotech/shopify-client/ajax";
import { binarySearch } from "@redotech/util/binary-search";
import { ReactNode, memo, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import { useDebounce } from "usehooks-ts";
import { RedoToggleElement } from "./cart-app";
import { RedoCheckbox } from "./checkbox";
import type { RedoCheckboxElement } from "./checkbox-app";
import { useLocationManager } from "./location";
import { TAPCART_SELECTOR, addCheckbox } from "./product-page";
import { RedoRoot } from "./root";
import {
  DEFAULT_CART_COUNT_SELECTOR,
  DEFAULT_PRODUCT_SELECTOR,
  DEFAULT_TOGGLE_CLICK_SELECTOR,
  DEFAULT_TOGGLE_SELECTOR,
} from "./selectors";
import { ExtensionShopifyClientContext, shopifyProductHandle } from "./shopify";
import { DeluxeToggleArray, RedoToggle } from "./toggle";
import {
  REDO_DISABLE_TAGS,
  getSellingPlanId,
  isPackageProtection,
  isRedoItem,
  productMatchesTags,
} from "./utils";

export function initialize(
  shopifyClient: ExtensionShopifyClient,
  autocheck: WidgetAutocheck,
  cartEnabled: boolean,
  cartChangeListener: Observable<Symbol>,
) {
  if (document.querySelector("#redo-container")) {
    return;
  }
  RedoToggleElement.register();
  const redoContainer = document.createElement("div");
  redoContainer.id = "redo-container";
  document.head.appendChild(redoContainer);
  const root = createRoot(redoContainer);
  root.render(
    <RedoRoot
      widgetId={window.redoWidgetId}
      extensionShopifyClient={shopifyClient}
    >
      <RedoManager
        autocheck={autocheck}
        cartEnabled={cartEnabled}
        cartChangeListener={cartChangeListener}
      />
    </RedoRoot>,
  );

  window.removeRedo = () => {
    root.unmount();
    redoContainer.remove();
  };
}

interface RedoManagerProps {
  autocheck: WidgetAutocheck;
  cartEnabled: boolean;
  cartChangeListener: Observable<Symbol>;
}

type SELECTOR_PLACEMENT = "before" | "after" | "prepend" | "append";
const DEFAULT_TOGGLE_PLACEMENT: SELECTOR_PLACEMENT = "before";

const RETURN_TOGGLE_STATE_KEY = "return-toggle-state";
const PROTECTION_TOGGLE_STATE_KEY = "protection-toggle-state";
const SINGLE_TOGGLE_STATE_KEY = "single-toggle-state";
const SELLING_PLAN_ID = "redo-selling-plan-id";
const RETURN_TYPE = "return";
const PACKAGE_PROTECTION_TYPE = "package_protection";
const BOTH_TYPE = "return,package_protection";

let submitButtonSelector = [
  ".redo-submit-button",
  ".picky-add-selected-to-cart-button", // PickyStory bundles
  'form[action="/cart/add"] [type=submit]',
  ".ajax-submit",
  ".js-add-to-cart",
  ".add-to-cart-btn",
  ".product-buy-buttons--cta",
  ".ProductForm__AddToCart", // pcheebum
].join(", ");

const DEFAULT_CART_COUNT_MODIFICATION = {
  selector: DEFAULT_CART_COUNT_SELECTOR,
  search: "\\d+",
  replace: "%count%",
};

const cartItemKey = (items: ShopifyCartItem[] | undefined) =>
  items
    ?.map((i) => `${i.id}-${i.quantity}`)
    .sort()
    .join(",") || "";

const setLocalStorageWithExpiry = (key: string, value: any) => {
  const ttl = 60 * 60 * 1000; // 1 hour
  const now = new Date();
  const item = {
    value,
    expiry: now.getTime() + ttl,
  };
  localStorage.setItem(key, JSON.stringify(item));
};

const getLocalStorageWithExpiry = (key: string) => {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) {
    return null;
  }
  const item = JSON.parse(itemStr);
  const now = new Date();
  if (now.getTime() > item.expiry) {
    localStorage.removeItem(key);
    return null;
  }
  return item.value;
};

const RedoManager = memo(
  ({ autocheck, cartEnabled, cartChangeListener }: RedoManagerProps) => {
    const locationManager = useLocationManager();
    const shopifyClient = useRequiredContext(ExtensionShopifyClientContext);
    const onTapcart = shopifyClient.type === ShopifyClientType.TAPCART;
    const widgetClient = useRequiredContext(WidgetClientContext);
    const merchantPaid = autocheck.paidModel === "merchant_paid";

    const feesLoad = useLoad(async (signal) => {
      const fees = await getFees(widgetClient, { signal });
      // Cache the handle so we can fetch it concurrently on the next page load.
      setLocalStorageWithExpiry("redo-handle", fees.handle);
      if (fees.returnProduct?.handle) {
        setLocalStorageWithExpiry("return-handle", fees.returnProduct.handle);
      }
      if (fees.packageProduct?.handle) {
        setLocalStorageWithExpiry("package-handle", fees.packageProduct.handle);
      }
      if (fees.bothProduct?.handle) {
        setLocalStorageWithExpiry("both-handle", fees.bothProduct.handle);
      }

      return fees;
    }, []);
    const fees = feesLoad.value;
    useEffect(() => {
      if ("error" in feesLoad) {
        console.error(feesLoad.error);
      }
    }, [feesLoad.error]);

    useEffect(() => {
      if (!fees || !autocheck.hideRedoProduct?.enabled) {
        return;
      }
      const products = [
        fees.packageProduct,
        fees.returnProduct,
        fees.bothProduct,
      ];
      const productSelector =
        autocheck.hideRedoProduct.productSelector || DEFAULT_PRODUCT_SELECTOR;
      const selectors = products.map((product) =>
        product
          ? productSelector
              .replaceAll("%handle%", product.handle || "")
              ?.replaceAll(`%product_id%`, String(product.productId) || "")
              ?.replaceAll(
                "%variant_id%",
                product.variants?.length >= 1
                  ? String(product.variants[0].variant_id)
                  : "",
              )
          : null,
      );
      let selector = selectors.filter(Boolean).join(", ");
      if (!selector) {
        return;
      }
      const style = document.createElement("style");
      style.innerText = `${selector} {display: none !important;}`;
      document.head.appendChild(style);
      return () => {
        style.remove();
      };
    }, [fees]);

    // Initialize selling plan for redo state.
    useEffect(() => {
      if (!getLocalStorageWithExpiry(SELLING_PLAN_ID)) {
        setLocalStorageWithExpiry(SELLING_PLAN_ID, "initial");
      }
    }, []);

    const countryLoad = useLoad(
      async (signal) => {
        if (!autocheck.countries?.length) {
          return null;
        }
        const result =
          globalThis.Shopify?.country ||
          (await locationManager.getCountry(signal));
        /*
        const predictedCountry = window.Shopify?.country;
        if (
          result.trim().toLowerCase() !== predictedCountry?.trim().toLowerCase()
        ) {
          postLocationDiscrepancy(
            widgetClient,
            { signal },
            result,
            predictedCountry ?? "<empty prediction>",
          );
        }
        */
        return result;
      },
      [autocheck, locationManager],
    );

    const validCountry =
      countryLoad.value === null ||
      autocheck.countries.includes(countryLoad.value!);

    const redoLock = useRef(false);
    const checkboxState = useRef(autocheck.redoAutoCheck);
    const [cartChecked, setCartChecked] = useState(false);
    const [redoInCart, setInCart] = useState(false);
    const [singleToggleState, setSingleToggleVal] = useState(false);
    const [returnToggleState, setReturnToggleVal] = useState(false);
    const [protectionToggleState, setProtectionToggleVal] = useState(false);
    const [cartEligibleReturns, setCartEligibleReturns] = useState(false);
    const [cartEligibleProtection, setCartEligibleProtection] = useState(false);
    const [checkbox, setCheckbox] = useState<RedoCheckboxElement | null>(null);
    const [toggles, setToggles] = useState<RedoToggleElement[]>([]);
    const [cartCurrency, setCartCurrency] = useState<Currency>(UsdCurrency);
    const [cartItems, setCartItems] = useState<ShopifyCartItem[]>([]);
    const cartItemsRef = useRef<ShopifyCartItem[]>([]);
    const [cartRefreshSignal, setCartRefresh] = useState(Symbol());
    const [checkboxRefresh, setCheckboxRefresh] = useState(Symbol());
    const cartRefresh = useDebounce(cartRefreshSignal, 500);
    const singleToggleVal = useDebounce(singleToggleState, 500);
    const returnToggleVal = useDebounce(returnToggleState, 500);
    const protectionToggleVal = useDebounce(protectionToggleState, 500);

    const checkboxLabelOnly =
      cartEnabled || autocheck.checkoutExtensionEnabled || merchantPaid;
    const hasTwoOptions =
      autocheck.attachmentStrategy === "double-toggle" ||
      autocheck.attachmentStrategy === "drop-down";

    const invalidProductTags = useMemo(
      () =>
        [...(autocheck.finalSaleTags || []), ...REDO_DISABLE_TAGS].map((s) =>
          s.toLowerCase(),
        ),
      [],
    );

    const handle = fees?.handle || getLocalStorageWithExpiry("redo-handle");
    let returnHandle =
      fees?.returnProduct?.handle || getLocalStorageWithExpiry("return-handle");
    let packageHandle =
      fees?.packageProduct?.handle ||
      getLocalStorageWithExpiry("package-handle");
    let bothHandle =
      fees?.bothProduct?.handle || getLocalStorageWithExpiry("both-handle");

    // uses product handles to load shopify items
    const redoProductLoad = useLoad(
      async (signal) => {
        if (!handle) return null;
        if (
          autocheck.packageProtection.splitProducts &&
          returnHandle &&
          packageHandle &&
          bothHandle
        ) {
          let promises = [shopifyClient.productGetJS(returnHandle, signal)];
          promises.push(shopifyClient.productGetJS(packageHandle, signal));
          promises.push(shopifyClient.productGetJS(bothHandle, signal));

          let shopifyProducts = await Promise.all(promises);
          for (let product of shopifyProducts) {
            product.variants.sort((a, b) => a.price - b.price);
          }

          return {
            mainShopifyProduct: shopifyProducts[2],
            returnShopifyProduct: shopifyProducts[0],
            packageShopifyProduct: shopifyProducts[1],
            bothShopifyProduct: shopifyProducts[2],
          };
        }
        const product = await shopifyClient.productGetJS(handle, signal);
        product.variants.sort((a, b) => a.price - b.price);
        return {
          mainShopifyProduct: product,
          returnShopifyProduct: null,
          packageShopifyProduct: null,
          bothShopifyProduct: null,
        };
      },
      [handle, returnHandle, packageHandle, bothHandle],
    );

    useEffect(() => {
      if (redoProductLoad.error) {
        console.error("Error getting redo product", redoProductLoad.error);
      }
    }, [redoProductLoad.error]);

    const mainRedoProduct = redoProductLoad.value?.mainShopifyProduct;
    const returnRedoProduct =
      redoProductLoad.value?.returnShopifyProduct ?? null;
    const packageRedoProduct =
      redoProductLoad.value?.packageShopifyProduct ?? null;
    const bothRedoProduct = redoProductLoad.value?.bothShopifyProduct ?? null;

    const productTypeMap = new Map<number, string>();

    if (mainRedoProduct && fees) {
      const productTypes = [
        {
          product: mainRedoProduct,
          type: fees.mainType,
        },
        { product: returnRedoProduct, type: RETURN_TYPE },
        { product: packageRedoProduct, type: PACKAGE_PROTECTION_TYPE },
        { product: bothRedoProduct, type: BOTH_TYPE },
      ];
      for (let p of productTypes) {
        if (p.product) productTypeMap.set(p.product.id, p.type);
      }
    }

    function getCorrectlyPricedVariant(
      cartItems: ShopifyCartItem[],
      product: ShopifyProductJS,
      productType: string | undefined,
    ) {
      if (product.variants.length === 1 || productType === RETURN_TYPE) {
        return product.variants[0];
      }
      // Todo: this will need to consider whether an item is Package protectable or not
      const cartValue = onTapcart
        ? window["Tapcart"].variables.cart.subtotal
        : cartItems.reduce(
            (acc, item) =>
              isRedoItem(item) ? acc : acc + (item.line_price || 0),
            0,
          );

      let calculatedCoveragePrice = 0;
      const cartPercentageTotal =
        cartValue * autocheck.packageProtection.percentage;

      // Return + package Protection
      if (productType === BOTH_TYPE) {
        calculatedCoveragePrice =
          product.price_min + // This is the base price with no package protection
          cartPercentageTotal; // This is the cost of the package protection
      }
      // Only package protection
      else if (productType === PACKAGE_PROTECTION_TYPE) {
        calculatedCoveragePrice = cartPercentageTotal;
      }

      const variants = product.variants;
      // Binary search for the variant with the price closest to the calculatedCoveragePrice.
      return binarySearch(variants, calculatedCoveragePrice, (v) => v.price);
    }

    function determineRedoProduct(
      mainRedoProduct: ShopifyProductJS,
      returnRedoProduct: ShopifyProductJS | null,
      packageRedoProduct: ShopifyProductJS | null,
      bothRedoProduct: ShopifyProductJS | null,
      fees: WidgetFees,
      autocheck: WidgetAutocheck,
    ) {
      if (
        fees.splitProducts &&
        autocheck.coverageEnabled &&
        autocheck.packageProtection.coverageEnabled &&
        bothRedoProduct &&
        returnRedoProduct &&
        packageRedoProduct
      ) {
        if (cartEligibleReturns && cartEligibleProtection) {
          mainRedoProduct = bothRedoProduct;
          if (hasTwoOptions) {
            if (returnToggleState && !protectionToggleState) {
              mainRedoProduct = returnRedoProduct;
            } else if (!returnToggleState && protectionToggleState) {
              mainRedoProduct = packageRedoProduct;
            }
          }
        } else if (cartEligibleProtection) {
          mainRedoProduct = packageRedoProduct;
        } else {
          mainRedoProduct = returnRedoProduct;
        }
      }
      return mainRedoProduct;
    }

    const getVariant = useHandler(
      (
        cartItems: ShopifyCartItem[],
        mainRedoProduct: ShopifyProductJS,
        returnRedoProduct: ShopifyProductJS | null,
        packageRedoProduct: ShopifyProductJS | null,
        bothRedoProduct: ShopifyProductJS | null,
        fees: WidgetFees,
        autocheck: WidgetAutocheck,
      ) => {
        let product = determineRedoProduct(
          mainRedoProduct,
          returnRedoProduct,
          packageRedoProduct,
          bothRedoProduct,
          fees,
          autocheck,
        );

        return getCorrectlyPricedVariant(
          cartItems,
          product,
          productTypeMap.get(product.id),
        );
      },
    );

    // Finds expected variant every time anything changes.
    const { id: expectedVariantID, price: redoPrice } = useMemo(() => {
      if (!fees || !mainRedoProduct || !cartChecked)
        return { id: null, price: 0 };

      if (checkboxLabelOnly) {
        if (
          fees.splitProducts &&
          autocheck.packageProtection.coverageEnabled &&
          hasTwoOptions
        ) {
          if (!returnToggleState && !protectionToggleState) {
            return { id: 0, price: 0 };
          }
        } else if (!singleToggleState) {
          return { id: 0, price: 0 };
        }
      }

      const variant = getVariant(
        cartItems,
        mainRedoProduct,
        returnRedoProduct,
        packageRedoProduct,
        bothRedoProduct,
        fees,
        autocheck,
      );
      return {
        id: variant.id,
        price: onTapcart ? variant.price : variant.price / 100,
      };
    }, [
      fees,
      mainRedoProduct,
      returnRedoProduct,
      packageRedoProduct,
      bothRedoProduct,
      cartItems,
      redoInCart,
      cartRefresh,
      cartChecked,
      cartEligibleReturns,
      cartEligibleProtection,
      returnToggleVal,
      protectionToggleVal,
      singleToggleVal,
    ]);

    // Finds and stores all places toggles should be rendered on the page
    const renderToggles = useHandler(() => {
      if (!cartEnabled || merchantPaid) {
        return;
      }
      let toggles = Array.from(
        document.querySelectorAll(
          onTapcart
            ? TAPCART_SELECTOR
            : autocheck.cartToggle.cartToggleSelector ||
                DEFAULT_TOGGLE_SELECTOR,
        ),
      ).map((container) => {
        let toggle: RedoToggleElement;
        switch (
          autocheck.cartToggle.cartTogglePlacement ||
          DEFAULT_TOGGLE_PLACEMENT
        ) {
          case "before":
          case "after":
            toggle = container.parentElement?.querySelector(
              RedoToggleElement.TAG,
            ) as RedoToggleElement;
            if (toggle) {
              return toggle;
            }
            break;
          case "prepend":
          case "append":
            toggle = container.querySelector(
              RedoToggleElement.TAG,
            ) as RedoToggleElement;
            if (toggle) {
              return toggle;
            }
            break;
        }
        toggle = document.createElement(
          RedoToggleElement.TAG,
        ) as RedoToggleElement;
        toggle.id = `redo-toggle-${Math.random().toString(36).slice(5)}`;

        const onDisconnect = () => {
          toggle.removeEventListener("disconnect", onDisconnect);
          setTimeout(renderToggles, 1000);
        };
        toggle.addEventListener("disconnect", onDisconnect);

        switch (
          autocheck.cartToggle.cartTogglePlacement ||
          DEFAULT_TOGGLE_PLACEMENT
        ) {
          case "before":
            container.insertAdjacentElement("beforebegin", toggle);
            break;
          case "after":
            container.insertAdjacentElement("afterend", toggle);
            break;
          case "prepend":
            container.insertAdjacentElement("afterbegin", toggle);
            break;
          case "append":
            container.insertAdjacentElement("beforeend", toggle);
            break;
        }
        return toggle;
      });

      setToggles(toggles);
    });

    const addRedo = useHandler(async (event?: Event, force = false) => {
      if (
        merchantPaid ||
        (redoInCart && !force) ||
        redoLock.current === true ||
        !validCountry ||
        !expectedVariantID
      ) {
        return;
      }
      try {
        redoLock.current = true;
        setInCart(true);
        const cartParams = new URLSearchParams();
        if (!onTapcart) {
          switch (Shopify.shop) {
            case "birdiethreads.myshopify.com":
              cartParams.append("ref_icart", "true");
              break;
            case "thedressoutlet.myshopify.com":
              cartParams.append("sections", "cart-drawer,cart-icon-bubble");
              break;
          }

          if (
            Shopify.shop === "the-vintage-gentlemen.myshopify.com" ||
            Shopify.shop === "www-lets-playground-shopify-com.myshopify.com"
          ) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            Rebuy.Cart.addItem({
              id: expectedVariantID,
              quantity: 1,
            });
            redoLock.current = false;
            return;
          }
        }

        event?.preventDefault();
        event?.stopPropagation();
        await shopifyClient.cartAdd(
          {
            items: [{ id: expectedVariantID, quantity: 1 }],
          },
          cartParams,
        );

        renderCart();

        if (!event) {
          return;
        }

        console.debug(`Resubmitting event ${event.type}`);
        if (
          typeof Rebuy !== "undefined" ||
          Shopify.shop === "megalashacademy.myshopify.com"
        ) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
        if (event.target instanceof HTMLElement && event.type === "click") {
          event.target.click();
        } else {
          event.target!.dispatchEvent(new Event(event.type));
        }
      } catch (e) {
        console.error("Error adding redo to the cart", e);
        // Refresh the cart so we know if it's actually in there or not
        setCartRefresh(Symbol());
      } finally {
        redoLock.current = false;
      }
    });

    const removeRedo = useHandler(async (manual: boolean) => {
      try {
        redoLock.current = true;
        // Optimistically set the toggle to false.
        setInCart(false);
        if (manual) {
        }
        const cart = await shopifyClient.cartGet();
        if (!cart?.items?.length) {
          return;
        }
        for (const item of cart.items) {
          if (isRedoItem(item)) {
            await shopifyClient.removeItemFromCart(item, item.quantity);
          }
        }
      } catch (e) {
        console.error("Error removing redo from the cart", e);
        // Refresh the cart so we know if it's actually in there or not
        setCartRefresh(Symbol());
      } finally {
        redoLock.current = false;
        renderCart();
      }
    });

    const onToggleChange = useHandler(
      (value: boolean, manual: boolean = true) => {
        setSingleToggleVal(value);
        const state = value ? "enabled" : "disabled";
        setLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY, state);
      },
    );

    const onReturnToggleChange = useHandler(
      (value: boolean, manual: boolean = true) => {
        setReturnToggleVal(value);
        const state = value ? "enabled" : "disabled";
        setLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY, state);
      },
    );

    const onProtectionToggleChange = useHandler(
      (value: boolean, manual: boolean = true) => {
        setProtectionToggleVal(value);
        const state = value ? "enabled" : "disabled";
        setLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY, state);
      },
    );

    const onCheckboxChange = useHandler((value: boolean) => {
      checkboxState.current = value;
    });

    const onModalAdd = useHandler(() => {
      if (!redoInCart) {
        addRedo();
      }
    });

    // If the customer is in an invalid country but they have Redo in their cart,
    // remove it.
    useEffect(() => {
      if (countryLoad.value && !validCountry && cartChecked && redoInCart) {
        removeRedo(false);
      }
    }, [redoInCart, validCountry, countryLoad.value, cartChecked]);

    const getTags = (product: ShopifyProduct) => {
      return product.tags.toLowerCase().split(/, */).filter(Boolean);
    };
    const ensurePromise = useRef<Promise<any> | null>(null);
    // Makes sure the correct variant of redo is in the cart.
    const ensureRedoLoad = useLoad(
      async (signal) => {
        while (ensurePromise.current !== null) {
          try {
            await ensurePromise.current;
          } catch (e) {
            // Who cares
          }
        }
        if (!cartChecked || expectedVariantID === null || !checkboxLabelOnly) {
          return;
        }

        ensurePromise.current = (async () => {
          let foundCorrectVariant = false;
          const cart = await shopifyClient.cartGet(signal);
          for (const item of cart.items) {
            try {
              if (isRedoItem(item)) {
                // Remove any incorrect variants
                if (item.variant_id !== expectedVariantID) {
                  await shopifyClient.removeItemFromCart(
                    item,
                    item.quantity,
                    signal,
                  );
                } else {
                  foundCorrectVariant = true;
                  if (item.quantity > 1) {
                    // If there is more than one, make sure to fix that.
                    await shopifyClient.removeItemFromCart(
                      item,
                      item.quantity - 1,
                      signal,
                    );
                  }
                }
              }
            } catch (e) {
              console.error("Error in ensure product");
            }
          }
          if (!foundCorrectVariant && expectedVariantID !== 0) {
            await addRedo(undefined, true);
          }
        })();

        try {
          return await ensurePromise.current;
        } finally {
          ensurePromise.current = null;
        }
      },
      [expectedVariantID, cartItems],
    );

    // Check current cart and render toggles.
    useLoad(
      async (signal) => {
        if (!fees || !validCountry || !mainRedoProduct) {
          return;
        }
        const cart = await shopifyClient.cartGet(signal);
        setCartCurrency(new Currency(cart.currency));
        // Check if the cart has actually changed.
        let inCart = !!redoInCart;
        const inCartLastCheck = !!redoInCart;
        let hasValidReturnProduct = cartEligibleReturns;
        let hasValidProtectionProduct = cartEligibleProtection;
        let redoItem: ShopifyCartItem | null = null;
        let hasPackageAndSplit =
          autocheck.packageProtection.coverageEnabled &&
          autocheck.packageProtection.splitProducts;
        // Checks if cart line items have changed or if any selling plans have changed.
        if (
          cartItemKey(cart.items) !== cartItemKey(cartItems) ||
          sellingPlanChange(cart.items, cartItems)
        ) {
          // Set these to false until we actually check the cart.
          inCart = false;
          hasValidReturnProduct = false;
          hasValidProtectionProduct = false;
          if (cart?.items?.length) {
            for (const item of cart.items) {
              if (isRedoItem(item)) {
                inCart = true;
                redoItem = item;
                continue;
              }
              hasValidProtectionProduct = true;

              // No need to check product tags if we already have one valid product.
              if (hasValidReturnProduct || !item.handle) {
                continue;
              }
              const product = await shopifyClient.productGet(
                item.handle,
                signal,
              );

              let excludeByCollection: boolean = false;
              if (autocheck.coverageExcludeCollections.length) {
                if (getLocalStorageWithExpiry(item.id.toString()) !== null) {
                  excludeByCollection =
                    getLocalStorageWithExpiry(item.id.toString()) === "true";
                } else {
                  const collections = await getCollections(widgetClient, {
                    signal,
                    productId: product.id.toString(),
                  });
                  if (
                    collections.some((collection) =>
                      autocheck.coverageExcludeCollections.includes(collection),
                    )
                  ) {
                    excludeByCollection = true;
                    setLocalStorageWithExpiry(product.id.toString(), "true");
                  } else {
                    setLocalStorageWithExpiry(product.id.toString(), "false");
                  }
                }
              }

              if (
                !productMatchesTags(getTags(product), invalidProductTags) &&
                !excludeByCollection
              ) {
                hasValidReturnProduct = true;
              }
            }
          }
          if (signal.aborted) {
            return;
          }
          if (!redoItem) {
            setLocalStorageWithExpiry(SELLING_PLAN_ID, "initial");
          }

          // If the merchant uses recharge, check if we can add redo as a subscription
          if (inCart && redoItem && autocheck.usesRecharge && !onTapcart) {
            //TODO: implement Tapcart + Recharge if possible
            // Check if the customer changed the subscription, if they did we won't update it for them
            if (
              getLocalStorageWithExpiry(SELLING_PLAN_ID) === "initial" ||
              getLocalStorageWithExpiry(SELLING_PLAN_ID) ===
                redoItem?.selling_plan_allocation?.selling_plan?.id.toString()
            ) {
              const sellingPlanId = await getSellingPlanId(redoItem, cart);
              const redoIndexInCart = cart.items.findIndex(
                (e) => e.id === redoItem?.id,
              );
              if (sellingPlanId) {
                setLocalStorageWithExpiry(
                  SELLING_PLAN_ID,
                  sellingPlanId.toString(),
                );
                await shopifyClient.updateCartItemSellingPlan(
                  redoIndexInCart + 1,
                  sellingPlanId,
                  1,
                );
              } else if (
                getLocalStorageWithExpiry(SELLING_PLAN_ID) !== "initial"
              ) {
                await shopifyClient.updateCartItemSellingPlan(
                  redoIndexInCart + 1,
                  null,
                  1,
                );
              }
            }
          }
          const returnState =
            getLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY) === "enabled";
          const protectionState =
            getLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY) ===
            "enabled";
          const singleState =
            getLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY) === "enabled";

          let redoForciblyRemoved = false;
          let hasEligibleProduct = false;

          const returnsEligibleAndCovered =
            hasValidReturnProduct && autocheck.coverageEnabled;
          const packageProtectionEligibleAndCovered =
            autocheck.packageProtection?.coverageEnabled &&
            hasValidProtectionProduct;

          if (
            !autocheck.packageProtection?.splitProducts &&
            autocheck.coverageEnabled &&
            autocheck.packageProtection?.coverageEnabled
          ) {
            hasEligibleProduct =
              returnsEligibleAndCovered && packageProtectionEligibleAndCovered;
          } else {
            hasEligibleProduct =
              returnsEligibleAndCovered || packageProtectionEligibleAndCovered;
          }

          const redoWasRemoved =
            !inCart && hasEligibleProduct && inCartLastCheck;
          if (hasPackageAndSplit && hasTwoOptions) {
            if (
              (returnState || protectionState) &&
              redoWasRemoved &&
              !autocheck.hideRedoProduct?.enabled
            ) {
              redoForciblyRemoved = true;
            }
          } else if (
            singleState &&
            redoWasRemoved &&
            !autocheck.hideRedoProduct?.enabled
          ) {
            redoForciblyRemoved = true;
          }

          // If redo was removed by customer set all to false
          if (redoForciblyRemoved && !ensureRedoLoad.pending) {
            if (hasPackageAndSplit && hasTwoOptions) {
              if (returnToggleVal) {
                setReturnToggleVal(false);
                setLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY, "disabled");
              }
              if (protectionToggleVal) {
                setProtectionToggleVal(false);
                setLocalStorageWithExpiry(
                  PROTECTION_TOGGLE_STATE_KEY,
                  "disabled",
                );
              }
            } else {
              if (singleToggleVal) {
                setSingleToggleVal(false);
                setLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY, "disabled");
              }
            }
          }

          const returnDisabled =
            getLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY) === "disabled";
          const protectionDisabled =
            getLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY) ===
            "disabled";
          const singleDisabled =
            getLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY) === "disabled";

          setCartEligibleReturns(hasValidReturnProduct);
          setCartEligibleProtection(hasValidProtectionProduct);

          if (cartEnabled && !redoForciblyRemoved) {
            // if cart is eligible for adding a redo item add it by changing toggles
            if (
              hasPackageAndSplit &&
              autocheck.coverageEnabled &&
              hasTwoOptions
            ) {
              if (
                hasValidReturnProduct &&
                autocheck.redoAutoCheck &&
                !returnToggleVal &&
                !returnDisabled
              ) {
                setReturnToggleVal(true);
                setLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY, "enabled");
              }
              if (
                hasValidProtectionProduct &&
                autocheck.packageProtectionAutoCheck &&
                !protectionToggleVal &&
                !protectionDisabled
              ) {
                setProtectionToggleVal(true);
                setLocalStorageWithExpiry(
                  PROTECTION_TOGGLE_STATE_KEY,
                  "enabled",
                );
              }
            } else if (
              autocheck.redoAutoCheck &&
              hasEligibleProduct &&
              !singleToggleVal &&
              !singleDisabled
            ) {
              setSingleToggleVal(true);
              setLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY, "enabled");
            }
          }
          setCartItems(cart.items);
          cartItemsRef.current = cart.items;
        }

        if (hasValidReturnProduct || hasValidProtectionProduct) {
          renderToggles();
        }
        setInCart(inCart);
        setCartChecked(true);
      },
      [cartRefresh, fees, validCountry, mainRedoProduct],
    );

    // Update the cart counters whenever the cart items change.
    useEffect(() => {
      if (!autocheck.hideRedoProduct?.enabled || !cartItems?.length) {
        return;
      }

      const count = cartItems.reduce((acc, item) => {
        if (isRedoItem(item) || isPackageProtection(item)) {
          return acc;
        }
        return acc + item.quantity;
      }, 0);

      const line_item_count = cartItems.reduce((acc, item) => {
        if (isRedoItem(item) || isPackageProtection(item)) {
          return acc;
        }
        return acc + 1;
      }, 0);

      const cartModifications =
        (autocheck.hideRedoProduct?.cartCountModifications?.length ?? 0) > 0
          ? autocheck.hideRedoProduct?.cartCountModifications || [
              DEFAULT_CART_COUNT_MODIFICATION,
            ]
          : [DEFAULT_CART_COUNT_MODIFICATION];

      for (const mod of cartModifications) {
        let search = new RegExp(mod.search, "g");
        let replace = mod.replace
          .replaceAll("%count%", count.toString())
          .replaceAll("%line_item_count%", line_item_count.toString());
        const elements = document.querySelectorAll(mod.selector);
        for (const element of elements) {
          element.innerHTML = element.innerHTML.replace(search, replace);
          // Jack's Surf Shop
          if (element.hasAttribute("data-header-cart-count")) {
            element.setAttribute("data-header-cart-count", count.toString());
          }
        }
      }
    }, [cartItems, cartRefresh]);

    // Listen for click events and refresh toggles.
    useEventListener(
      document,
      "click",
      (e) => {
        if (
          e.target instanceof Element &&
          // Check if the click was one of the elements that could trigger a cart change or open.
          ((autocheck.cartToggle.cartClickListenerSelector &&
            e.target.matches(autocheck.cartToggle.cartClickListenerSelector)) ||
            e.target.matches(DEFAULT_TOGGLE_CLICK_SELECTOR))
        ) {
          setTimeout(() => setCartRefresh(Symbol()), 500);
          setTimeout(() => setCartRefresh(Symbol()), 2000);
        }
      },
      true,
    );

    // Listen for cart changes and refresh the cart
    useEffect(() => {
      const subscription = cartChangeListener.subscribe(() => {
        setCartRefresh(Symbol());
      });
      return () => subscription.unsubscribe();
    }, []);

    // Handle navigation using Temporal.InstantClick which doesn't actually reload the page.
    // This will trigger the checkbox to be added if it needs to be, as well as re-rendering the toggle.
    useEffect(() => {
      if (window.InstantClick) {
        window.InstantClick.on("change", () => {
          setCheckboxRefresh(Symbol());
          setCartRefresh(Symbol());
        });
      }
    }, []);

    const isOnProductPage = () => {
      return onTapcart
        ? !!window["Tapcart"].variables.product
        : location.href.includes("/products/");
    };

    // Add checkbox if on product page
    useEffect(() => {
      if (document.querySelector("redo-checkbox") || isOnProductPage()) {
        if (autocheck.submitButtonSelector) {
          submitButtonSelector += `, ${autocheck.submitButtonSelector}`;
        }
        setCheckbox(addCheckbox(autocheck, onTapcart));
      } else if (checkbox) {
        setCheckbox(null);
      }
    }, [checkboxRefresh]);

    let validProductLoad = useLoad(
      async (signal) => {
        if (
          checkbox &&
          (document.querySelector("redo-checkbox") || isOnProductPage())
        ) {
          const handle = onTapcart
            ? window["Tapcart"].variables.product.handle
            : shopifyProductHandle(location.href);
          if (!handle) {
            return false;
          }
          const product = await shopifyClient.productGet(handle, signal);

          let excludeByCollection: boolean = false;
          if (autocheck.coverageExcludeCollections.length) {
            if (getLocalStorageWithExpiry(product.id.toString()) !== null) {
              excludeByCollection =
                getLocalStorageWithExpiry(product.id.toString()) === "true";
            } else {
              const collections = await getCollections(widgetClient, {
                signal,
                productId: product.id.toString(),
              });
              if (
                collections.some((collection) =>
                  autocheck.coverageExcludeCollections.includes(collection),
                )
              ) {
                excludeByCollection = true;
                setLocalStorageWithExpiry(product.id.toString(), "true");
              } else {
                setLocalStorageWithExpiry(product.id.toString(), "false");
              }
            }
          }
          const productTags = onTapcart
            ? window["Tapcart"].variables.product.tags
            : getTags(await shopifyClient.productGet(handle, signal));
          return (
            !productMatchesTags(productTags, invalidProductTags) &&
            !excludeByCollection
          );
        }
        return false;
      },
      [checkbox],
    );
    useEffect(() => {
      if (validProductLoad.error) {
        console.error(validProductLoad.error);
      }
    }, [validProductLoad.error]);

    const showCheckbox =
      (checkboxLabelOnly || !redoInCart) &&
      validProductLoad.value === true &&
      cartChecked;
    // Add checkbox event handlers
    useEffect(() => {
      if (!checkbox || !showCheckbox || checkboxLabelOnly) {
        return;
      }

      // Boolean to make sure we only add redo once for these handlers.
      // Once we add it, they will all be removed on the next re-render
      // because showCheckbox will be false.
      let added = false;
      const form = checkbox.closest("form");
      const formEventHandler = (e: SubmitEvent) => {
        if (checkboxState.current && !added) {
          added = true;
          addRedo(e);
        }
      };
      const rebuyHandler = (e: any) => {
        if (checkboxState.current && !added) {
          added = true;
          // Don't add redo when we're already adding redo.
          if (e?.detail?.item?.variant_id === fees?.variantId) {
            return;
          }
          addRedo();
        }
      };
      const clickEventHandler = (e: MouseEvent) => {
        if (
          e.target instanceof Element &&
          Shopify.theme.name === "Dawn" &&
          e.target.closest(".shopify-payment-button__button")
        ) {
          // Buy it now button for the dawn theme
          const quantityElement = document.querySelector(
            '.quantity__input, input[type="number"]',
          ) as HTMLInputElement | null;
          const quantity = quantityElement?.value || "1";
          const currentSelectedVariant = (
            document.querySelector(
              ".product__pickup-availabilities",
            ) as HTMLElement
          ).dataset.variantId;
          e.preventDefault();
          e.stopPropagation();
          window.location.href = `https://${
            Shopify.shop
          }/cart/${currentSelectedVariant}:${quantity},${
            fees!.variantId
          }:1?traffic_source=buy_now`;
        } else if (
          e.target instanceof Element &&
          e.target.closest(submitButtonSelector) &&
          checkboxState.current &&
          !added
        ) {
          added = true;
          addRedo(e);
        }
      };

      form?.addEventListener("submit", formEventHandler);
      document.addEventListener("rebuy:cart.add", rebuyHandler);
      document.addEventListener("click", clickEventHandler, true);

      return () => {
        form?.removeEventListener("submit", formEventHandler);
        document.removeEventListener("rebuy:cart.add", rebuyHandler);
        document.removeEventListener("click", clickEventHandler);
      };
    }, [checkbox, showCheckbox, cartEnabled]);

    if (!fees) {
      return <></>;
    }

    if (
      !merchantPaid &&
      (autocheck.disableRedoForCustomer ||
        (autocheck.hideCheckbox &&
          !autocheck.cartToggle.enabled &&
          !onTapcart) ||
        !autocheck.enabled ||
        !validCountry ||
        (autocheck.hideCheckbox && !cartChecked))
    ) {
      return <></>;
    }

    const hasProtectionReturnsAndSplit =
      autocheck.packageProtection.coverageEnabled &&
      autocheck.coverageEnabled &&
      autocheck.packageProtection.splitProducts;

    // determine toggle fields
    let toggleText = autocheck.checkboxText;
    let allToggleOptions = defaultToggleTextOptions(autocheck.coverage);
    const defaults = defaultToggleTextOptions(autocheck.coverage);
    if (autocheck.toggleTextOptions) {
      allToggleOptions.returnToggle = fillEmptyFieldsWithDefault(
        autocheck.toggleTextOptions.returnToggle,
        defaults.returnToggle,
      );
      allToggleOptions.packageProtectionToggle = fillEmptyFieldsWithDefault(
        autocheck.toggleTextOptions.packageProtectionToggle,
        defaults.packageProtectionToggle,
      );
      allToggleOptions.bothProductToggle = fillEmptyFieldsWithDefault(
        autocheck.toggleTextOptions.bothProductToggle,
        defaults.bothProductToggle,
      );
    }

    let singleToggleFields;
    if (fees.mainType === "return") {
      singleToggleFields = allToggleOptions.returnToggle;
    } else if (fees.mainType === "return,package_protection") {
      singleToggleFields = allToggleOptions.bothProductToggle;
    } else if (fees.mainType === "package_protection") {
      singleToggleFields = allToggleOptions.packageProtectionToggle;
    }

    if (hasProtectionReturnsAndSplit) {
      if (cartEligibleReturns && cartEligibleProtection) {
        singleToggleFields = allToggleOptions.bothProductToggle;
      } else if (cartEligibleProtection) {
        singleToggleFields = allToggleOptions.packageProtectionToggle;
      } else {
        singleToggleFields = allToggleOptions.packageProtectionToggle;
      }
    }

    if (fees.splitProducts || autocheck.packageProtection.coverageEnabled) {
      toggleText = singleToggleFields.titleText;
    }

    const image_url = autocheck.logo || DEFAULT_IMAGE_URL;
    const priceDivisor = onTapcart ? 1 : 100;

    const generateSingleToggleElement = (): ReactNode => {
      if (mainRedoProduct) {
        const redoProduct = determineRedoProduct(
          mainRedoProduct,
          returnRedoProduct,
          packageRedoProduct,
          bothRedoProduct,
          fees,
          autocheck,
        );
        const price =
          getCorrectlyPricedVariant(
            cartItems,
            redoProduct,
            productTypeMap.get(redoProduct.id),
          ).price / priceDivisor;
        const deluxeVar = false;
        const toggle = (
          <RedoToggle
            color={autocheck.textColor}
            coverage={autocheck.coverage}
            html={autocheck.html}
            fontSize={autocheck.toggleFontSize}
            value={singleToggleState}
            paymentModel={autocheck.paidModel}
            price={new Money(price || +fees.price, cartCurrency)}
            branding={!autocheck.hideCheckboxBranding}
            onChange={onToggleChange}
            toggleText={toggleText}
            toggleFields={singleToggleFields}
            toggleTextAfterRedo={autocheck.checkboxTextAfterRedo}
            isDelux={deluxeVar}
          />
        );

        if (deluxeVar) {
          return (
            <DeluxeToggleArray image_url={image_url}>
              {toggle}
            </DeluxeToggleArray>
          );
        }
        return toggle;
      }
      return <></>;
    };

    const generateDoubleToggleElement = (): ReactNode => {
      let returnToggle = <></>;
      let packageProtectionToggle = <></>;

      if (cartEligibleReturns && returnRedoProduct) {
        const price =
          getCorrectlyPricedVariant(cartItems, returnRedoProduct, RETURN_TYPE)
            .price / priceDivisor;
        returnToggle = (
          <RedoToggle
            color={autocheck.textColor}
            coverage={autocheck.coverage}
            html={autocheck.html}
            fontSize={autocheck.toggleFontSize}
            value={returnToggleState}
            paymentModel={autocheck.paidModel}
            price={new Money(price || +fees.price, cartCurrency)}
            branding={!autocheck.hideCheckboxBranding}
            onChange={onReturnToggleChange}
            toggleText={"returns"}
            toggleFields={allToggleOptions.returnToggle}
            toggleTextAfterRedo={autocheck.checkboxTextAfterRedo}
            isDelux={true}
          />
        );
      }

      if (cartEligibleProtection && packageRedoProduct) {
        const price =
          getCorrectlyPricedVariant(
            cartItems,
            packageRedoProduct,
            PACKAGE_PROTECTION_TYPE,
          ).price / priceDivisor;
        packageProtectionToggle = (
          <RedoToggle
            color={autocheck.textColor}
            coverage={autocheck.coverage}
            html={autocheck.html}
            fontSize={autocheck.toggleFontSize}
            value={protectionToggleState}
            paymentModel={autocheck.paidModel}
            price={new Money(price || +fees.price, cartCurrency)}
            branding={!autocheck.hideCheckboxBranding}
            onChange={onProtectionToggleChange}
            toggleText={"Package Protection"}
            toggleFields={allToggleOptions.packageProtectionToggle}
            toggleTextAfterRedo={autocheck.checkboxTextAfterRedo}
            isDelux={true}
          />
        );
      }

      return (
        <>
          <DeluxeToggleArray image_url={image_url}>
            {returnToggle}
            {packageProtectionToggle}
          </DeluxeToggleArray>
        </>
      );
    };
    let checkoutReactElement = generateSingleToggleElement;

    if (
      hasProtectionReturnsAndSplit &&
      autocheck.attachmentStrategy === "double-toggle"
    ) {
      checkoutReactElement = generateDoubleToggleElement;
    }

    let showToggles = false;
    const retunEligibleAndCovered =
      cartEligibleReturns && autocheck.coverageEnabled;
    const packageEligibleAndCovered =
      autocheck.packageProtection?.coverageEnabled && cartEligibleProtection;
    const hasBothCoverage =
      autocheck.coverageEnabled && autocheck.packageProtection?.coverageEnabled;

    if (hasBothCoverage && !autocheck.packageProtection?.splitProducts) {
      showToggles = retunEligibleAndCovered && packageEligibleAndCovered;
    } else {
      showToggles = retunEligibleAndCovered || packageEligibleAndCovered;
    }

    fireReady();
    return (
      <>
        {checkbox && showCheckbox
          ? createPortal(
              <ModalRootContext.Provider value={checkbox.modalContainer}>
                <RedoCheckbox
                  color={autocheck.textColor}
                  coverage={autocheck.coverage}
                  modalTextAdjustments={autocheck.modalTextAdjustments}
                  fontSize={autocheck.checkboxFontSize}
                  html={autocheck.html}
                  initial={autocheck.redoAutoCheck}
                  paymentModel={autocheck.paidModel}
                  price={
                    !autocheck.packageProtection.coverageEnabled
                      ? new Money(redoPrice || +fees.price, cartCurrency)
                      : undefined
                  }
                  branding={!autocheck.hideCheckboxBranding}
                  onAdd={onModalAdd}
                  onChange={onCheckboxChange}
                  checkboxText={autocheck.checkboxText}
                  checkboxTextAfterRedo={autocheck.checkboxTextAfterRedo}
                  hideCheckbox={onTapcart ? false : autocheck.hideCheckbox}
                  labelOnly={checkboxLabelOnly}
                  infoLink={autocheck.infoIconLink}
                  showInfo={!onTapcart && autocheck.coverageEnabled}
                  customCss={autocheck.customPdpCss}
                />
              </ModalRootContext.Provider>,
              checkbox.root,
            )
          : null}
        {!(!!onTapcart && isOnProductPage()) &&
          showToggles &&
          toggles.map((toggle) =>
            createPortal(checkoutReactElement(), toggle.root, toggle.id),
          )}
      </>
    );
  },
);

import * as amplitude from "@amplitude/analytics-browser";
import { Observable } from "rxjs";
import { renderCart } from "./render-cart";
function fireReady() {
  if (localStorage.getItem("redo-ready")) {
    return;
  }
  try {
    localStorage.setItem("redo-ready", "true");
    performance.mark("redo-ready");
    let timings = performance.measure(
      "redo-ready",
      "domInteractive",
      "redo-ready",
    );
    amplitude.logEvent("redo-ready", {
      time: timings.duration,
    });
  } catch (e) {
    console.warn(e);
  }
}

const sellingPlanChange = (
  firstItems: ShopifyCartItem[] | undefined,
  secondItems: ShopifyCartItem[] | undefined,
) => {
  let hasChanged = false;
  for (const item of firstItems || []) {
    const secondItem = secondItems?.find((i) => i.id === item.id);
    if (
      item?.selling_plan_allocation?.selling_plan?.id !==
      secondItem?.selling_plan_allocation?.selling_plan?.id
    ) {
      hasChanged = true;
      break;
    }
  }
  return hasChanged;
};
